import React, { Component } from 'react';
import { connect, useSelector } from 'react-redux';

import { useScopedApiFilters } from 'hooks/useAdminGroup/useScopedApiFilters';
import type { ChartFilters } from 'hooks/useAdminGroup/applyLocationScope';
import MultiSelect from 'components/multi_select';
import CallFilters from 'components/call_filters';
import type { CallFilterOption } from 'components/call_filters';
import ButtonPopover from 'components/button_popover';
import TabbedControls from 'components/tabbed_controls';
import { bindControlsActions } from 'actions/controls_actions';
import * as selectors from 'modules/reporting';
import DateRangePickerWithTheme from 'analytics-ui/DateRangePicker/DateRangePicker';

/** The slice of `state.controls.reporting` this component reads. */
interface ReportingControlsState {
  filterType?: { id: string };
  filters?: unknown[];
  filterOptions?: unknown[];
  callTypes?: unknown[];
  callTypeOptions?: unknown[];
  realTime?: boolean;
  timeRange?: Record<string, unknown>;
  timezone?: string;
  section?: string;
  metadata?: { filter_totals_url: string };
  callFilter?: string;
  callFilterOptions?: CallFilterOption[];
  apiFilters?: ChartFilters;
}

interface ReportControlsProps {
  /** Current filter type */
  filterType: { id: string };
  /** Selected filters */
  filters: unknown[];
  /** Availble filters */
  filterOptions: unknown[];
  /** Are we in real time mode? */
  realTime: boolean;
  /** Time range for the section */
  timeRange: Record<string, unknown>;
  /** User timezone */
  timezone: string;
  /** The current section of the current page */
  section: string;
  /** Metadata for the reporting section */
  metadata: { filter_totals_url: string };
  /** Current call filter */
  callFilter: string;
  /** Available call filters */
  callFilterOptions: CallFilterOption[];
  /** Breadcrumb-scoped apiFilters (scope_locations) from the wrapper */
  scopedApiFilters: ChartFilters;
  /** True while the scope/hierarchy is still resolving -- hold fetches */
  scopeHold: boolean;
}

/**
 * The state slice is optional-everything (pre-init store), but ReportPage
 * only renders this component once `controlsState.ready` -- the class props
 * stay required, matching the JS version's runtime contract.
 */
type ReportControlsStateProps = Omit<
  ReportControlsProps,
  'scopedApiFilters' | 'scopeHold'
> & {
  callTypes?: unknown[];
  callTypeOptions?: unknown[];
};

const mapStateToProps = (state: unknown): ReportControlsStateProps => {
  const reportingState = selectors.reportingSelector(state) as ReportingControlsState;
  return {
    filterType: reportingState.filterType,
    filters: reportingState.filters,
    filterOptions: reportingState.filterOptions,
    callTypes: reportingState.callTypes,
    callTypeOptions: reportingState.callTypeOptions,
    realTime: reportingState.realTime,
    timeRange: reportingState.timeRange,
    timezone: reportingState.timezone,
    section: reportingState.section,
    metadata: reportingState.metadata,
    callFilter: reportingState.callFilter,
    callFilterOptions: reportingState.callFilterOptions,
  } as ReportControlsStateProps;
};

class ReportControls extends Component<ReportControlsProps> {
  actions = bindControlsActions('reporting') as Record<
    string,
    (...args: unknown[]) => void
  >;

  render() {
    const {
      filterType,
      filters,
      filterOptions,
      realTime,
      timeRange,
      timezone,
      section,
      metadata,
      callFilter,
      callFilterOptions,
      scopedApiFilters,
      scopeHold,
    } = this.props;
    let buttonPopover: React.ReactNode;

    if (filterOptions.length) {
      const filtersSelect = (
        <MultiSelect
          searchBar
          options={filterOptions}
          onChange={this.actions.setFilters}
          initialSelected={filters}
        />
      );
      const tabbedControls = (
        <TabbedControls
          tabs={[
            { icon: 'icon-custom-filter', label: filterType.id, content: filtersSelect },
          ]}
        />
      );
      buttonPopover = (
        <ButtonPopover
          popoverClass="controls-popover report-controls-popover"
          iconClass="icon-custom-settings"
          popoverBody={tabbedControls}
        />
      );
    }

    return (
      <div className="report-controls">
        <div className="report-controls__left">
          <DateRangePickerWithTheme
            withRealTimeIcon
            timezone={timezone}
            dateRange={timeRange}
            isRealTime={realTime}
            className="date-range-popover"
            onChange={this.actions.setTimeRange}
            onRealTimeChange={this.actions.setRealTime}
            theme={{
              dateRangePicker: 'date-range',
            }}
          />
          {buttonPopover}
        </div>

        <CallFilters
          timeRange={timeRange}
          realTime={realTime}
          filterTotalsUrl={metadata.filter_totals_url}
          section={section}
          filter={callFilter}
          filterOptions={callFilterOptions}
          filters={scopedApiFilters}
          scopeHold={scopeHold}
        />
      </div>
    );
  }
}

const ConnectedReportControls = connect(mapStateToProps)(ReportControls);

// The call-type chip strip (CallFilters) POSTs metadata.filter_totals_url
// (total-count) on its own fetch path, outside ReportPageChart, so the
// breadcrumb scope is attached here. Deliberately NOT wrapped in
// LocationScopeGate: gating would unmount the date picker. CallFilters holds
// its fetch while scopeHold is true and refetches when it clears.
const ReportControlsWithScope = (props: Record<string, unknown>) => {
  const reportingState = useSelector(
    (state: { controls?: Record<string, ReportingControlsState | undefined> }): ReportingControlsState =>
      (state.controls && state.controls.reporting) || {},
  );
  // isEmptyIntersection is deliberately unused here: firing the "-1" sentinel
  // is fail-closed and cheap, and the chips then show dashes -- the truthful
  // answer for an empty scope ∩ filter intersection.
  const { scope, apiFilters, hold } = useScopedApiFilters(
    reportingState.apiFilters,
    reportingState.filterOptions,
  );

  return (
    <ConnectedReportControls
      {...props}
      scopedApiFilters={apiFilters}
      scopeHold={hold || scope.isLoading}
    />
  );
};

export default ReportControlsWithScope;
